import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectUserInfo } from 'redux/userInfo/userInfoSlice';
import { useNotificationProvider } from 'hooks';
import {
	ActiveOverstay,
	getActiveOverstays,
} from 'api/canpark/overstays/getActiveOverstays';
import { dismissOverstay as dismissOverstayApi } from 'api/canpark/overstays/dismissOverstay';
import { dismissAllOverstays as dismissAllOverstaysApi } from 'api/canpark/overstays/dismissAllOverstays';
import { getParkingLocationsByCustomerId } from 'api/canpark/parkingLocations/getParkingLocationsByCustomerId';
import { ParkingLocation } from 'containers/pages/CANpark/types/parkingLocation';
import { useActiveVehicle } from '../context/ActiveVehicleProvider';
import { useCustomers } from './useCustomers';

export const useOverstayNotifications = () => {
	const { customers } = useCustomers();
	const userInfo = useSelector(selectUserInfo)?.userInfo;
	const currentUserId = Number(userInfo?.userId) || 0;
	const { notifyError } = useNotificationProvider();
	const { currentSearch } = useActiveVehicle();

	const [overstays, setOverstays] = useState<ActiveOverstay[]>([]);
	const [loading, setLoading] = useState(false);
	const [hasFetched, setHasFetched] = useState(false);
	const [parkingLocationsById, setParkingLocationsById] = useState<
		Record<string, ParkingLocation>
	>({});

	const companyIdsKey = customers.map((c) => c.Id).join(',');

	const fetchOverstaysAndLots = useCallback(async () => {
		if (customers.length === 0) return;
		setLoading(true);
		try {
			const list = await getActiveOverstays(customers.map((c) => c.Id));
			setOverstays(list);

			// Fetch parking locations ONLY for the customers that have overstays
			// (not all 600+ — we don't need lots for customers with nothing active).
			const uniqueCompanyIds = Array.from(
				new Set(list.map((o) => o.companyId).filter(Boolean)),
			);
			if (uniqueCompanyIds.length > 0) {
				const results = await Promise.all(
					uniqueCompanyIds.map((id) =>
						getParkingLocationsByCustomerId(id).catch(() => []),
					),
				);
				const map: Record<string, ParkingLocation> = {};
				results.flat().forEach((loc) => {
					map[loc.id] = loc;
				});
				setParkingLocationsById(map);
			} else {
				setParkingLocationsById({});
			}
		} catch (err) {
			// eslint-disable-next-line no-console
			console.error('Overstay fetch failed', err);
		} finally {
			setLoading(false);
			setHasFetched(true);
		}
	}, [customers]);

	// Single fetch on mount (and whenever the customer set changes). No polling —
	// user can refresh the page if they want fresher data.
	useEffect(() => {
		fetchOverstaysAndLots();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [companyIdsKey]);

	// Refetch whenever the vehicle drawer closes (operator returned to the home
	// screen — a ticket may have just been issued, which could clear an overstay).
	const vehicleDrawerOpenRef = useRef(false);
	useEffect(() => {
		if (currentSearch) {
			vehicleDrawerOpenRef.current = true;
		} else if (vehicleDrawerOpenRef.current) {
			vehicleDrawerOpenRef.current = false;
			fetchOverstaysAndLots();
		}
	}, [currentSearch, fetchOverstaysAndLots]);

	const dismiss = useCallback(
		async (ticketNumber: string) => {
			const prev = overstays;
			setOverstays((cur) => cur.filter((o) => o.ticketNumber !== ticketNumber));
			try {
				await dismissOverstayApi(ticketNumber, currentUserId);
			} catch (err) {
				notifyError('Failed to dismiss overstay');
				setOverstays(prev);
			}
		},
		[overstays, currentUserId, notifyError],
	);

	const dismissAll = useCallback(async () => {
		const prev = overstays;
		const ticketNumbers = prev.map((o) => o.ticketNumber);
		setOverstays([]);
		try {
			await dismissAllOverstaysApi(ticketNumbers, currentUserId);
		} catch (err) {
			notifyError('Failed to dismiss overstays');
			setOverstays(prev);
		}
	}, [overstays, currentUserId, notifyError]);

	const getParkingLocation = useCallback(
		(id: string) => parkingLocationsById[id],
		[parkingLocationsById],
	);

	// Gate by per-lot `enableOverstayNotifications`. An overstay only counts if
	// the lot it came from has the flag enabled. Overstays for unknown lots
	// (lot fetch in flight or 404) are dropped — they'll re-appear once the
	// lot data arrives and the flag confirms enrollment.
	const filteredOverstays = overstays.filter(
		(o) => parkingLocationsById[o.parkingLocationId]?.enableOverstayNotifications,
	);

	return {
		overstays: filteredOverstays,
		loading,
		hasFetched,
		refresh: fetchOverstaysAndLots,
		dismiss,
		dismissAll,
		getParkingLocation,
	};
};
